<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="1200"
    :closable="!saving"
    :closeOnEscape="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '32rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div
          class="crc-form-dialog-header__icon"
          :style="{ backgroundColor: normalizedTagColor + '22', color: normalizedTagColor }"
        >
          <i class="pi pi-building text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ modalTitle }}
          </div>
        </div>
        <Tag
          :value="draft.name?.trim() ? draft.name : 'Preview'"
          :style="tagStyle"
          class="crc-form-dialog-header__badge !border-0 !px-3 !py-1 rounded-lg text-xs font-semibold"
        />
      </div>
    </template>

    <form @submit.prevent="handleSubmit">

      <!-- Name + Reports To -->
      <section class="crc-form-dialog-section crc-form-dialog-fields">
          <div class="flex flex-col gap-1.5">
            <FloatLabel variant="on" class="w-full">
              <InputText
                id="department-name"
                v-model="draft.name"
                :disabled="saving"
                maxlength="120"
                autocomplete="off"
                :invalid="(submitted && !draft.name?.trim()) || Boolean(nameError)"
                class="w-full rounded-md"
              />
              <label for="department-name" class="text-sm font-medium">
                Name <span class="text-red-500">*</span>
              </label>
            </FloatLabel>
            <small v-if="submitted && !draft.name?.trim()" class="text-xs text-red-500 pl-1">
              Please enter a department name.
            </small>
            <small v-else-if="nameError" class="text-xs text-red-500 pl-1">
              {{ nameError }}
            </small>
          </div>

          <div class="flex flex-col gap-1.5">
            <FloatLabel variant="on" class="w-full">
              <Select
                inputId="department-reporting-branch"
                v-model="draft.reporting_branch"
                :options="reportingBranchOptions"
                optionLabel="label"
                optionValue="value"
                :disabled="saving || loadingOptions"
                :loading="loadingOptions"
                class="w-full"
                inputClass="w-full rounded-md"
                filter
                filterPlaceholder="Search executive..."
              />
              <label for="department-reporting-branch" class="text-sm font-medium">
                Reports To
              </label>
            </FloatLabel>
            <small v-if="selectedReportingExecutiveHint" class="text-xs text-surface-500 pl-1">
              {{ selectedReportingExecutiveHint }}
            </small>
            <small v-else class="text-xs text-surface-500 pl-1">
              Sets the organogram branch. Leave empty to keep this department off the C-Suite tree.
            </small>
          </div>

          <div class="flex flex-col gap-1.5">
            <FloatLabel variant="on" class="w-full">
              <Select
                inputId="department-head-user"
                v-model="draft.head_user_id"
                :options="headUserOptions"
                optionLabel="label"
                optionValue="value"
                :disabled="saving || loadingOptions || !canAssignHead"
                :loading="loadingOptions"
                showClear
                class="w-full"
                inputClass="w-full rounded-md"
                filter
                filterPlaceholder="Search employee..."
              />
              <label for="department-head-user" class="text-sm font-medium">
                Department Head
              </label>
            </FloatLabel>
            <small v-if="!canAssignHead" class="text-xs text-surface-500 pl-1">
              Save the department first, then assign a head from its employees.
            </small>
            <small v-else-if="autoHeadHint" class="text-xs text-surface-500 pl-1">
              {{ autoHeadHint }}
            </small>
          </div>
      </section>

      <!-- Colors -->
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-section__label">
          Appearance
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <HexColorField
            v-model="draft.tag_color"
            label="Background"
            input-id="department-tag-color"
            fallback-color="#64748B"
            :disabled="saving"
            :invalid="submitted"
            @change="onBackgroundColorChange"
          />
          <HexColorField
            v-model="draft.tag_text_color"
            label="Text"
            input-id="department-tag-text-color"
            fallback-color="#FFFFFF"
            :disabled="saving"
            :invalid="submitted"
          />
        </div>
      </section>

      <!-- Status -->
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-status">
          <div>
            <div class="crc-form-dialog-status__title">Active</div>
            <div class="crc-form-dialog-status__hint">
              Inactive departments are hidden from profile options.
            </div>
          </div>
          <ToggleSwitch inputId="isActive" v-model="draft.is_active" :disabled="saving" />
        </div>
      </section>

      <Message v-if="generalError" severity="error" :closable="false" class="mx-4 mb-3">
        {{ generalError }}
      </Message>

      <!-- Footer -->
      <div class="crc-form-dialog-footer">
        <Button
          v-if="draft.id && canDelete"
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          text
          :disabled="saving || !canDelete"
          @click="confirmDelete"
        />
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="onClose"
          />
          <Button
            type="submit"
            :label="mode === 'new' ? 'Create' : 'Save'"
            :icon="mode === 'new' ? 'pi pi-plus' : 'pi pi-check'"
            :loading="saving"
            :disabled="saving || !canSubmit || !isFormValid"
          />
        </div>
      </div>

    </form>
  </Dialog>
</template>

<script setup>
import { isOrgReferenceActive } from '@/features/settings/utils/settingsOrgReferenceUtils';
import { useSettingsDraftGuard } from '@/features/settings/composables/useSettingsDraftGuard';
import { computed, reactive, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import Tag from 'primevue/tag';
import Message from 'primevue/message';
import FloatLabel from 'primevue/floatlabel';
import Select from 'primevue/select';
import HexColorField from '@/components/common/HexColorField.vue';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';
import { getCSuiteRoleMeta } from '@/constants/cSuiteRoles';
import { fetchDepartmentEditorOptions } from '@/api/departmentsApi';
import {
  addHash,
  isValidHexWithHash,
  normalizeHexWithHash,
  pickContrastingTextColor,
} from '@/utils/hexColor';

const emit = defineEmits(['update:visible', 'save', 'delete']);

const props = defineProps({
  visible: { type: Boolean, default: false },
  department: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  canCreate: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  canDelete: { type: Boolean, default: false },
  /** `{ code, message }` from the failed save; keeps the dialog open with an inline hint. */
  saveError: { type: Object, default: null }
});

const visibleProxy = computed({
  get: () => props.visible,
  set: (v) => emit('update:visible', v)
});

const mode = ref('edit');
const submitted = ref(false);
const dismissedError = ref(false);
const loadingOptions = ref(false);
const editorOptions = ref({
  reporting_executives: [],
  head_candidates: [],
  auto_head: null,
});

const draft = reactive({
  id: null,
  name: '',
  reporting_branch: '',
  head_user_id: null,
  tag_color: '#64748B',
  tag_text_color: '#FFFFFF',
  is_active: true
});

const canAssignHead = computed(() => mode.value === 'edit' && Number(draft.id) > 0);

const reportingBranchOptions = computed(() => {
  const options = (editorOptions.value.reporting_executives || []).map((exec) => ({
    label: exec.label,
    value: exec.reporting_branch,
    executiveName: exec.name,
    roleTitle: exec.role_title,
  }));

  const current = String(draft.reporting_branch || '').trim();
  if (current && !options.some((item) => item.value === current)) {
    const meta = getCSuiteRoleMeta(current);
    options.unshift({
      label: `${meta?.title || current} — no executive assigned`,
      value: current,
      executiveName: null,
      roleTitle: meta?.title || current,
    });
  }

  options.push({
    label: 'No C-Suite line',
    value: '',
    executiveName: null,
    roleTitle: null,
  });

  return options;
});

const headUserOptions = computed(() => {
  const options = (editorOptions.value.head_candidates || []).map((candidate) => ({
    label: candidate.label,
    value: candidate.user_id,
  }));

  const currentHeadUserId = draft.head_user_id != null ? Number(draft.head_user_id) : null;
  if (
    currentHeadUserId
    && !options.some((item) => Number(item.value) === currentHeadUserId)
    && props.department?.department_head_name
  ) {
    const subtitle = props.department.department_head_position
      || props.department.department_head_grade
      || '';
    options.unshift({
      label: subtitle
        ? `${props.department.department_head_name} — ${subtitle}`
        : props.department.department_head_name,
      value: currentHeadUserId,
    });
  }

  return options;
});

const selectedReportingExecutiveHint = computed(() => {
  const selected = reportingBranchOptions.value.find(
    (item) => item.value === draft.reporting_branch,
  );
  if (!selected?.executiveName) return '';
  return `Organogram branch: ${selected.roleTitle || selected.value} (${selected.value})`;
});

const autoHeadHint = computed(() => {
  if (!canAssignHead.value || draft.head_user_id != null) return '';
  const autoHead = editorOptions.value.auto_head;
  if (!autoHead?.name) {
    return 'No employees in this department yet — head will stay empty until someone is assigned.';
  }
  return `Auto-detect would pick ${autoHead.label}. Choose an employee above to override.`;
});

const normalizedTagColor = computed(() =>
  normalizeHexWithHash(draft.tag_color, '#64748B')
);

const normalizedTagTextColor = computed(() =>
  normalizeHexWithHash(draft.tag_text_color, '#FFFFFF')
);

const onBackgroundColorChange = () => {
  draft.tag_text_color = pickContrastingTextColor(normalizedTagColor.value);
};

const modalTitle = computed(() => (mode.value === 'new' ? 'New Department' : 'Edit Department'));

const tagStyle = computed(() => ({
  backgroundColor: normalizedTagColor.value,
  color: normalizedTagTextColor.value
}));

const isFormValid = computed(() => {
  return (
    !!draft.name?.trim() &&
    isValidHexWithHash(normalizedTagColor.value) &&
    isValidHexWithHash(normalizedTagTextColor.value)
  );
});

const canSubmit = computed(() => (mode.value === 'new' ? props.canCreate : props.canEdit));

const activeError = computed(() =>
  dismissedError.value || props.saving ? null : props.saveError
);
const nameError = computed(() =>
  activeError.value?.code === 'validation.name_duplicate' ? activeError.value.message : ''
);
const generalError = computed(() =>
  activeError.value && !nameError.value ? activeError.value.message : ''
);

const fillDraftFromDepartment = (dept) => {
  if (!dept) { fillDraftNew(); return; }
  draft.id = dept.id ?? null;
  draft.name = dept.name ?? '';
  // Keep an existing department off the C-Suite tree instead of silently pinning it to CEO.
  draft.reporting_branch = dept.reporting_branch || '';
  draft.head_user_id = dept.head_user_id != null ? Number(dept.head_user_id) : null;
  draft.tag_color = addHash(dept.tag_color ?? '#64748B');
  draft.tag_text_color = addHash(dept.tag_text_color ?? '#FFFFFF');
  draft.is_active = isOrgReferenceActive(dept);
  if (!isValidHexWithHash(draft.tag_text_color)) {
    draft.tag_text_color = pickContrastingTextColor(normalizedTagColor.value);
  }
};

const fillDraftNew = () => {
  draft.id = null;
  draft.name = '';
  draft.reporting_branch = '';
  draft.head_user_id = null;
  draft.tag_color = '#64748B';
  draft.tag_text_color = '#FFFFFF';
  draft.is_active = true;
};

const loadEditorOptions = async () => {
  loadingOptions.value = true;
  try {
    editorOptions.value = await fetchDepartmentEditorOptions({
      departmentId: draft.id,
    });

    const executiveBranches = (editorOptions.value.reporting_executives || [])
      .map((exec) => exec.reporting_branch)
      .filter(Boolean);
    // Only replace a stale code that is no longer an executive option.
    // An empty line is intentional — do not snap a new department onto the first executive.
    if (
      mode.value === 'new'
      && draft.reporting_branch
      && executiveBranches.length
      && !executiveBranches.includes(draft.reporting_branch)
    ) {
      draft.reporting_branch = executiveBranches[0];
    }
  } catch {
    editorOptions.value = {
      reporting_executives: [],
      head_candidates: [],
      auto_head: null,
    };
  } finally {
    loadingOptions.value = false;
  }
};

watch(
  () => props.department,
  (val) => {
    if (visibleProxy.value && mode.value === 'edit') fillDraftFromDepartment(val);
  },
  { deep: true }
);

watch(() => props.saveError, () => { dismissedError.value = false; });

// Editing the fields the server complained about hides the stale message.
watch(() => draft.name, () => { dismissedError.value = true; });

watch(
  () => props.visible,
  async (open) => {
    if (!open) return;
    submitted.value = false;
    dismissedError.value = false;
    if (props.department) {
      mode.value = 'edit';
      fillDraftFromDepartment(props.department);
    } else {
      mode.value = 'new';
      fillDraftNew();
    }
    await loadEditorOptions();
  }
);

const onClose = () => {
  if (props.saving) return;
  visibleProxy.value = false;
  submitted.value = false;
};

// The parent closes the dialog once the request succeeds, so a failed save keeps the draft.
const handleSubmit = () => {
  submitted.value = true;
  if (!isFormValid.value || !canSubmit.value || props.saving) return;
  dismissedError.value = true;
  emit('save', {
    ...draft,
    reporting_branch: String(draft.reporting_branch || '').trim() || null,
    head_user_id: canAssignHead.value ? draft.head_user_id : null,
    tag_color: normalizedTagColor.value,
    tag_text_color: normalizedTagTextColor.value
  });
};

const confirmDelete = () => {
  if (!draft.id || !props.canDelete || props.saving) return;
  emit('delete', { id: draft.id });
};

defineExpose({
  openNew: () => {
    mode.value = 'new';
    fillDraftNew();
    visibleProxy.value = true;
  },
  openEdit: (dept) => {
    mode.value = 'edit';
    fillDraftFromDepartment(dept);
    visibleProxy.value = true;
  }
});
const draftGuard = useSettingsDraftGuard({
  draft, visible: () => props.visible, saving: () => props.saving,
});
</script>
